import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PartnerDevDialog from "./PartnerDevDialog";
import PartnerFormDialog from "./actions/PartnerFormDialog";
import { partnerHasChanges, validatePartnerForm } from "./actions/formValidation";
import {
  apiMessage,
  bodyCellSx,
  formatDateTime,
  generatePartnerApiKey,
  headCellSx,
  paperBorderSx,
  parseDiscount,
  parseExpiryDays,
} from "./partnerShared";
import { updateCampaignSettings } from "../../../contexts/services/admin/promoCodeServices";

const blankPartner = () => ({
  mode: "create",
  partner_name: "",
  discount_value: "20",
  expiry_days: "90",
  is_active: true,
  api_key: generatePartnerApiKey(),
  keyIsNew: true,
  applyDiscount: false,
  applyExpiry: false,
  original: null,
});

const partnerFromRow = (row) => ({
  mode: "edit",
  partner_name: row.partner_name,
  discount_value: String(row.discount_value ?? ""),
  expiry_days: String(row.expiry_days ?? ""),
  is_active: row.is_active !== false,
  api_key: "",
  keyIsNew: false,
  applyDiscount: false,
  applyExpiry: false,
  original: row,
});

export default function PartnersTab({
  campaigns,
  loading,
  error,
  busy,
  issuedKey,
  onIssuedKey,
  onDismissKey,
  onRefresh,
  onReview,
  onSnack,
}) {
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [copied, setCopied] = useState("");
  const [devPartner, setDevPartner] = useState(null);

  const copyText = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      window.setTimeout(() => setCopied(""), 2000);
    } catch {
      setFormError("Could not copy the key. Select it and copy it manually.");
    }
  };

  const askToSave = () => {
    if (!form || busy || !partnerHasChanges(form) || Object.keys(validatePartnerForm(form, campaigns)).length) return;
    setFormError("");
    const name = form.partner_name.trim().toLowerCase();
    const discount = parseDiscount(form.discount_value);
    const expiry = parseExpiryDays(form.expiry_days);
    const creating = form.mode === "create";
    const lines = [
      creating
        ? `Create partner ${name}.`
        : `Update partner ${name}.`,
      `New codes are ${discount}% off and last ${expiry} days.`,
      form.is_active ? "They can ask for new codes." : "They can't ask for new codes.",
    ];
    if (form.keyIsNew) {
      lines.push(
        creating
          ? "Copy the key and send it to them. We only keep the last four characters."
          : "The new key replaces the old one. The old key stops working after you save."
      );
    }
    if (form.applyDiscount) {
      lines.push("Codes that already exist, and discounts already on subscriptions, change to this percentage.");
    }
    if (form.applyExpiry) {
      lines.push("Unused codes get a new expiry, counted from the day they were created.");
    }
    const snapshot = {
      partner_name: name,
      discount_value: discount,
      expiry_days: expiry,
      is_active: form.is_active,
      ...(form.keyIsNew ? { api_key: form.api_key } : {}),
      ...(form.applyDiscount ? { apply_discount_to_existing: true } : {}),
      ...(form.applyExpiry ? { apply_expiry_to_issued: true } : {}),
    };
    onReview({
      title: creating ? "Create partner" : "Update partner",
      confirmLabel: creating ? "Create partner" : "Save changes",
      summary: (
        <Stack spacing={0.75}>
          {lines.map((line) => (
            <Typography key={line} variant='body2'>
              {line}
            </Typography>
          ))}
        </Stack>
      ),
      run: async (reason) => {
        try {
          await updateCampaignSettings({ ...snapshot, reason });
          setForm(null);
          if (snapshot.api_key) {
            onIssuedKey({ partner_name: name, api_key: snapshot.api_key });
          }
          onSnack({
            message: creating ? `${name} is ready.` : `${name} was updated.`,
          });
          await onRefresh();
        } catch (saveError) {
          setFormError(apiMessage(saveError, "Could not save this partner."));
        }
      },
    });
  };

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      {issuedKey?.api_key && (
        <Alert
          severity='success'
          sx={{ flexShrink: 0, maxHeight: "25%", overflow: "auto", overflowWrap: "anywhere" }}
          onClose={() => onDismissKey(null)}
          action={
            <Button
              color='inherit'
              size='small'
              onClick={() => copyText(issuedKey.api_key, "issued")}
            >
              {copied === "issued" ? "Copied" : "Copy"}
            </Button>
          }
        >
          Copy this key for {issuedKey.partner_name} now. You won't see the full key again:{" "}
          {issuedKey.api_key}
        </Alert>
      )}

      <Paper
        component='section'
        aria-labelledby='partners-title'
        elevation={0}
        sx={{
          ...paperBorderSx,
          boxShadow: "0 3px 16px rgba(30,20,50,0.025)",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            maxHeight: "55%",
            overflow: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Box sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent='space-between'
              gap={2}
            >
              <Stack direction='row' alignItems='center' gap={1}>
                <Typography id='partners-title' component='h2' variant='subtitle1' fontWeight={700}>
                  Partners
                </Typography>
                {!loading && !error && (
                  <Chip
                    size='small'
                    label={`${campaigns.length} ${campaigns.length === 1 ? "partner" : "partners"}`}
                    sx={{ height: 24, fontWeight: 600 }}
                  />
                )}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant='outlined'
                  startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <RefreshRoundedIcon />}
                  disabled={busy || loading}
                  onClick={onRefresh}
                >
                  Refresh
                </Button>
                <Button
                  variant='contained'
                  startIcon={<AddRoundedIcon />}
                  disabled={busy || loading || Boolean(error)}
                  onClick={() => {
                    setFormError("");
                    setForm(blankPartner());
                  }}
                  sx={{ boxShadow: "none", whiteSpace: "nowrap" }}
                >
                  Create partner
                </Button>
              </Stack>
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Each partner has its own discount, how long codes last, and a key.
            </Typography>
            {campaigns.length > 0 && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: { xs: "block", md: "none" }, mt: 1 }}
              >
                Scroll the table sideways to see the key and actions.
              </Typography>
            )}
          </Box>

          {error && (
            <Alert
              severity='error'
              variant='outlined'
              sx={{ m: 2 }}
              action={
                <Button color='inherit' size='small' onClick={onRefresh} disabled={busy || loading}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}
        </Box>

        {loading ? (
          <Stack
            role='status'
            direction='row'
            spacing={1.5}
            justifyContent='center'
            alignItems='center'
            sx={{ p: 5, flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <CircularProgress size={20} />
            <Typography variant='body2' color='text.secondary'>
              Loading partners...
            </Typography>
          </Stack>
        ) : campaigns.length === 0 && !error ? (
          <Stack alignItems='center' spacing={1} sx={{ px: 2, py: 5, textAlign: "center", flex: 1, minHeight: 0, justifyContent: "center", overflow: "auto" }}>
            <HandshakeOutlinedIcon sx={{ color: "text.disabled", fontSize: 32 }} />
            <Typography variant='subtitle2' fontWeight={700}>
              No partners
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Add a partner to get started.
            </Typography>
          </Stack>
        ) : (
          campaigns.length > 0 && (
          <TableContainer
            tabIndex={0}
            role='region'
            aria-label='Partners table'
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              overscrollBehavior: "contain",
              scrollbarGutter: "stable",
            }}
          >
            <Table
              size='small'
              stickyHeader
              aria-label='Partners'
              sx={{ minWidth: 720, "& tr:last-child td": { borderBottom: 0 } }}
            >
              <TableHead>
                <TableRow>
                  {["Partner", "Discount", "Code lasts", "Claims", "Key", "Updated", ""].map((label) => (
                    <TableCell
                      key={label || "actions"}
                      align={label ? "left" : "right"}
                      sx={{ ...headCellSx, bgcolor: "background.default" }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((row) => (
                  <TableRow key={row.partner_name} hover>
                    <TableCell sx={bodyCellSx}>
                      <Typography variant='body2' fontWeight={600}>
                        {row.partner_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{row.discount_value}%</TableCell>
                    <TableCell sx={bodyCellSx}>{row.expiry_days} days</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip
                        size='small'
                        label={row.is_active ? "On" : "Off"}
                        color={row.is_active ? "success" : "default"}
                        sx={{ fontWeight: 650 }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {row.has_api_key ? `····${row.api_key_hint || ""}` : "Missing"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{formatDateTime(row.updated_at)}</TableCell>
                    <TableCell align='right' sx={bodyCellSx}>
                      <Stack direction='row' justifyContent='flex-end' spacing={0.5}>
                        <Tooltip title='Dev tools'>
                          <IconButton
                            aria-label={`Dev tools for ${row.partner_name}`}
                            disabled={busy}
                            onClick={() => setDevPartner(row)}
                          >
                            <TerminalOutlinedIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Edit partner'>
                          <IconButton
                            aria-label={`Edit ${row.partner_name}`}
                            disabled={busy}
                            onClick={() => {
                              setFormError("");
                              setForm(partnerFromRow(row));
                            }}
                          >
                            <EditOutlinedIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )
        )}
      </Paper>

      <PartnerDevDialog
        partner={devPartner}
        issuedKey={issuedKey}
        onClose={() => setDevPartner(null)}
      />

      <PartnerFormDialog
        form={form}
        error={formError}
        campaigns={campaigns}
        busy={busy}
        copied={copied}
        onCopy={(value) => copyText(value, "form")}
        onFormChange={(next) => {
          setFormError("");
          setForm(next);
        }}
        onClose={() => setForm(null)}
        onReview={askToSave}
      />
    </Stack>
  );
}
